<template>
  <div class="no-select asset-container q-mx-sm relative-position">
    <div
      v-if="showLoadingOverlay"
      class="loading-overlay absolute absolute-full flex justify-center items-center"
    >
      <q-spinner-ios
        color="white"
        size="lg"
      />
    </div>
    <q-card
      v-if="isFailed"
      :class="{ 'cursor-pointer': fileCanBeDownloaded }"
      class="q-pa-xs row asset text-center justify-center items-center
         overflow-hidden-y"
      @click="onClickFailedAsset"
    >
      <div class="text-subtitle2 text-center q-ma-sm">
        {{ $t('sessionAssets.asset.textProcessingError') }}
        <span v-if="fileCanBeDownloaded">
          {{ $t('sessionAssets.asset.textProcessingErrorAction') }}
        </span>
      </div>
      <q-icon
        name="error"
        size="md"
        color="negative"
      />
    </q-card>
    <q-card
      v-else-if="isCancelled"
      :class="{ 'cursor-pointer': fileCanBeDownloaded }"
      class="q-pa-xs row asset text-center justify-center items-center
         overflow-hidden-y"
      @click="onClickCancelledAsset"
    >
      <div class="text-subtitle2 text-center q-ma-sm">
        {{ $t('sessionAssets.asset.textCancelledError') }}
        <span v-if="fileCanBeDownloaded">
          {{ $t('sessionAssets.asset.textCancelledErrorAction') }}
        </span>
      </div>
      <q-icon
        name="error"
        size="md"
        color="negative"
      />
    </q-card>
    <q-card
      v-else-if="isProcessing"
      class="asset text-center items-center q-pa-md"
    >
      <q-spinner
        size="lg"
        color="grey-6"
      />
      <div class="absolute-bottom text-subtitle2 text-center ellipsis q-ma-sm">
        {{ processingText }}
      </div>
    </q-card>
    <q-card
      v-else
      class="full-height v-ripple clickable cursor-pointer "
      @click="onClickAsset"
    >
      <q-img
        :src="assetImage"
        class="asset"
      >
        <q-icon
          v-if="!assetHasImage"
          :name="assetIcon"
          color="primary-variant"
          class="full-width q-mt-md"
          size="lg"
        />
        <div class="absolute-bottom items-center row all-pointer-events">
          <div class="col text-subtitle2 text-center ellipsis">
            {{ assetName }}
          </div>
          <div
            class="col-auto"
          >
            <q-btn
              flat
              round
              size="sm"
              icon="more_vert"
              @click.stop
            >
              <q-menu>
                <q-list>
                  <template
                    v-if="editable && assetType === 'video' && canChangeFile"
                  >
                    <q-item
                      v-ripple
                      v-close-popup
                      clickable
                      @click="onSetVideoOffset"
                    >
                      <q-item-section avatar>
                        <q-icon name="start" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessionAssets.asset.textItemSetVideoOffset') }}
                      </q-item-section>
                    </q-item>
                    <q-separator />
                  </template>
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onDownloadFile"
                  >
                    <q-item-section avatar>
                      <q-icon name="cloud_download" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessionAssets.asset.textItemDownload') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="editable && canChangeFile"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onRenameFile"
                  >
                    <q-item-section avatar>
                      <q-icon name="drive_file_rename_outline" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessionAssets.asset.textItemRename') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="editable && canChangeFile"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onDeleteFile"
                  >
                    <q-item-section avatar>
                      <q-icon name="delete" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessionAssets.asset.textItemDelete') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
        <template #loading>
          <q-spinner-ios color="white" />
        </template>
      </q-img>
    </q-card>
  </div>
</template>
<script>
import { storage } from 'boot/firebase';
import PreviewFile from 'components/ClientFiles/modals/PreviewFile';
import { getDownloadURL, ref } from 'firebase/storage';
import { chain, get, map } from 'lodash';
import { mapActions } from 'pinia';
import { openURL } from 'quasar';
import SessionFilesDB from 'src/fb/sessionFiles-db';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { broadcastChannel } from 'src/store/broadcastChannel';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'Asset',

  mixins: [Targets, Toolbox, Interactions],

  props: {
    allFiles: {
      type: Array,
      default: null,
    },

    asset: {
      type: Object,
      required: true,
    },

    session: {
      type: Object,
      required: true,
    },

    canChangeFile: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    currentTime: {
      type: Number,
      default: 0,
    },

    editable: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['delete-file', 'play-video', 'update-video-info'],

  data() {
    return {
      assetType: null,

      imageUrl: '',

      playbackTimer: null,

      fileDeleted: false,
    };
  },

  computed: {
    fileCanBeDownloaded() {
      return this.canChangeFile;
      // && (
      //   !['application/x-mpegURL'].includes(get(this.asset, 'type')
      //   || this.asset.downloadFilename));
    },

    assetHasImage() {
      return ['image', 'video'].includes(this.assetType);
    }, // assetHasImage()

    assetStatus() {
      return get(this.asset, 'status', '');
    },

    isProcessing() {
      return ['uploading', 'transcoding'].includes(this.assetStatus);
    }, // isProcessing()

    isFailed() {
      return this.assetStatus === 'failed';
    }, // isFailed()

    isCancelled() {
      return this.assetStatus === 'cancelled';
    }, // isCancelled()

    processingText() {
      return this.assetStatus === 'transcoding'
        ? this.$t('sessionAssets.asset.videoProcessingInProgress')
        : this.$t('sessionAssets.asset.uploadInProgress');
    }, // processingText()

    assetName() {
      return this.asset.displayName || this.downloadFilename;
    }, // assetName()

    assetIcon() {
      const icon = this.getFontAwesomeIconFromMIME(this.asset.type);
      return `far ${icon}`;
    },

    assetImage() {
      if (this.assetType === 'video') {
        return this.asset.coverphoto || 'video-placeholder.png';
      }
      return this.imageUrl;
    },

    downloadFilename() {
      return this.asset.downloadFilename || this.asset.originalFilename;
    }, // downloadFilename()

    showLoadingOverlay() {
      return this.loading && this.assetType === 'video' && !this.isCancelled && !this.isProcessing && !this.isFailed;
    }, // showLoadingOverlay()

  },

  watch: {
    asset: {
      immediate: true,
      async handler(newAsset) {
        // Prevent asset type and videoPlayerObject from being out of sync
        this.assetType = null;
        const assetType = this.getAssetType(newAsset);
        if (assetType === 'image' && newAsset.status !== 'uploading') {
          this.imageUrl = await this.accessFile(this.downloadFilename);
        }
        this.assetType = assetType;
      },
    },
  },

  beforeUnmount() {
    this.stopPlaybackTimer();

    if (this.fileDeleted) {
      this.$emit('delete-file', this.assetType);
    }
  },

  methods: {
    ...mapActions(auth, ['updateUserToken', 'getUserClaims']),
    ...mapActions(broadcastChannel, ['sendBroadcastMessage']),

    onClickAsset() {
      if (this.assetType === 'video') {
        this.onPlayVideo();
      } else {
        this.onPreviewFile();
      }
    }, // onClickAsset()

    async onPlayVideo() {
      this.$emit('play-video', await this.getVideoInfo());
    },

    onRenameFile() {
      let extension = '';
      let displayName = this.assetName;

      if (this.assetName.includes('.')) {
        extension = `.${chain(this.assetName)
          .split('.')
          .last()
          .value()}`;
        displayName = displayName.slice(0, -(extension.length));
      }

      this.$q.dialog({
        title: this.$t('sessionAssets.asset.renameCaption'),
        prompt: {
          model: displayName,
          isValid: (val) => val.trim().length > 2, // << here is the magic
          type: 'text',
        },
        cancel: true,
        persistent: true,
      }).onOk(async (filename) => {
        const sessionFilesDb = new SessionFilesDB(this.asset.session);
        this.$q.loading.show();

        try {
          await sessionFilesDb.set({ displayName: filename + extension }, this.asset.id);
        } finally {
          this.$q.loading.hide();
        }
      });
    }, // onRenameFile()

    onSetVideoOffset() {
      this.$q.dialog({
        title: this.$t('common.dialogTitleConfirm'),
        message: this.$t('sessionAssets.asset.videoOffsetMessage', {
          currentTime: this.formatDuration(this.currentTime),
        }),
        cancel: true,
        persistent: true,
      }).onOk(async () => {
        const sessionFilesDb = new SessionFilesDB(this.asset.session);
        this.$q.loading.show();

        try {
          await sessionFilesDb.set({ sessionTimeOffset: this.currentTime }, this.asset.id);
          this.$emit('update-video-info', await this.getVideoInfo());
        } finally {
          this.$q.loading.hide();
        }
      });
    }, // onSetVideoOffset()

    onDeleteFile() {
      this.$q.dialog({
        title: this.$t('common.dialogTitleConfirm'),
        message: this.$t('sessionAssets.asset.textDeleteMessage', { filename: this.assetName }),
        cancel: true,
        persistent: true,
      }).onOk(async () => {
        // Delete sessionFiles reference
        const sessionFilesDb = new SessionFilesDB(this.asset.session);
        this.$q.loading.show();

        try {
          this.fileDeleted = true;

          await Promise.all([
            sessionFilesDb.deleteSessionFile(this.asset),
            sessionFilesDb.delete(this.asset.id),
          ]);
        } catch (err) {
          this.fileDeleted = false;
          console.error('Error deleting file', err);
          logEvent(err);
        } finally {
          this.$q.loading.hide();
        }
      });
    }, // onDeleteFile()

    getAssetType(asset) {
      const type = get(asset, 'type');

      return type === 'application/x-mpegURL' ? 'video' : type.substr(0, type.indexOf('/'));
    },

    onStopped() {
      this.stopPlaybackTimer();
    }, // onStopped()

    onClickFailedAsset() {
      return this.onPreviewFile();
    }, // onClickFailedAsset()

    onClickCancelledAsset() {
      const sessionFilesDb = new SessionFilesDB(this.asset.session);
      sessionFilesDb.delete(this.asset.id);
    }, // onClickCancelledAsset()

    async onPreviewFile() {
      const fileUrl = await this.accessFile(this.downloadFilename);
      const allAssetFiles = map(this.allFiles, async (currentFile) => {
        const fileName = currentFile.downloadFilename || currentFile.originalFilename;
        const fileRef = ref(storage, `sessions/${this.session.id}/${fileName}`);
        const url = await getDownloadURL(fileRef);

        return ({
          fileName,
          fileType: currentFile.type,
          url,
          fileId: currentFile.id,
        });
      });
      const allFiles = await Promise.all(allAssetFiles);

      if (['image'].includes(this.assetType)
        || ['application/pdf'].includes(this.asset.type)) {
        return this.openComponentDialog(PreviewFile, {
          allFiles,
          currentFileId: this.asset.id,
        }, (previewData) => {
          if (previewData.download) {
            this.onDownloadFile();
          }
        });
      }

      openURL(fileUrl);
    }, // onPreviewFile()

    async onDownloadFile() {
      return window.open(await this.accessFile(this.downloadFilename), '_self');
    }, // onDownloadFile()

    async accessFile(filename = null) {
      const fileRef = ref(storage, `sessions/${this.session.id}/${filename}`);
      return getDownloadURL(fileRef);
    },

    stopPlaybackTimer() {
      if (this.playbackTimer !== null) {
        clearInterval(this.playbackTimer);
        this.playbackTimer = null;
      }
    },

    async getVideoInfo() {
      return {
        src: await this.accessFile(this.asset.originalFilename),
        type: this.asset.type,
        sessionTimeOffset: this.asset.sessionTimeOffset || 0,
      };
    }, // getVideoInfo()

  },

};
</script>
<style lang="scss" scoped>

.asset-container {
  width: 250px;

  .asset {
    height: 24vh;
  }

  .loading-overlay {
    background-color: rgba(0,0,0, .5);
    z-index: 1;
  }
}

</style>
